import React from "react";
import { Link } from "react-router-dom";
import { Users, ClipboardList, Flame, Award, ShieldCheck, Share2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { TeacherTestimonials } from "@/components/TeacherTestimonials";
import { TeacherEnquiryForm } from "@/components/TeacherEnquiryForm";

const TEACHER_SAMPLES = [
  { quote: "As a teacher I use The JT Hub with my whole class. The structure and progress tracking are brilliant.", name: "", role: "Secondary music teacher" },
];

const FEATURES = [
  { icon: KeyRound, t: "Join with a class code", d: "Create a class and students join with one simple code." },
  { icon: ClipboardList, t: "Set assignments", d: "Give lessons to the whole class, with optional due dates." },
  { icon: Flame, t: "See practice at a glance", d: "Each student's practice minutes this week and in total." },
  { icon: Award, t: "Track certificates", d: "See who has completed each course and earned a certificate." },
  { icon: Users, t: "Manage your students", d: "Add or remove students and follow their progress in one place." },
  { icon: ShieldCheck, t: "School-verified licences", d: "£400 per class per course, or £350 with a referral code." },
];

const ShareButton = () => {
  const share = async () => {
    const url = `${window.location.origin}/class-teaching`;
    const data = { title: "Class Teaching at The JT Hub", text: "Teach your class to read music in 10 lessons with Jason Thompson.", url };
    try {
      if (navigator.share) await navigator.share(data);
      else { await navigator.clipboard.writeText(url); toast.success("Link copied. Send it to a teacher you know."); }
    } catch { /* share dismissed */ }
  };
  return (
    <button type="button" onClick={share} data-testid="class-share-btn"
      className="inline-flex items-center gap-2 rounded-full border border-primary px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
      <Share2 size={16} /> Share with a teacher
    </button>
  );
};

export default function ClassTeaching() {
  const { user } = useAuth();
  const isStaff = user && (user.role === "teacher" || user.role === "admin");
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-8 lg:py-16" data-testid="class-teaching-page">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">For schools & music teachers</p>
        <h1 className="mt-3 max-w-3xl font-serif text-4xl tracking-tight sm:text-5xl lg:text-6xl">Teach your whole class to read music in 10 lessons</h1>
        <p className="mt-5 max-w-2xl text-muted-foreground">
          Bring Jason Thompson's courses for voice, guitar, drums and piano into your classroom. Your students learn with interactive lessons, play-along tracks and quizzes, and you follow their progress from one teacher dashboard.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {isStaff ? (
            <Link to="/teacher" data-testid="class-dashboard-btn" className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90">Go to my classes</Link>
          ) : (
            <Link to="/register?role=teacher" data-testid="class-signup-btn" className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90">Sign up as a teacher</Link>
          )}
          <Link to="/pricing" data-testid="class-pricing-btn" className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/60">See pricing</Link>
          <ShareButton />
        </div>
        <section className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="class-features">
          {FEATURES.map((f, i) => (
            <div key={f.t} className="rounded-2xl border border-border bg-card p-6" data-testid={`class-feature-${i}`}>
              <f.icon size={22} className="text-primary" />
              <h2 className="mt-4 font-serif text-lg">{f.t}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{f.d}</p>
            </div>
          ))}
        </section>
        <section className="mt-14 grid gap-8 rounded-3xl border border-border bg-card p-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-center" data-testid="class-video-section">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">See it in action</p>
            <h2 className="mt-3 font-serif text-lg md:text-lg">One minute on how Class Teaching works</h2>
            <p className="mt-3 text-sm text-muted-foreground">Watch how your students learn, practice and progress, and how you follow them from your dashboard.</p>
          </div>
          <video src="/media/promo/classroom-60-vertical.mp4" poster="/media/promo/classroom-60-vertical.jpg" controls playsInline preload="none"
            className="mx-auto aspect-[9/16] w-full max-w-[300px] rounded-2xl border border-border bg-black object-contain" data-testid="class-video" />
        </section>
        <section className="mt-14 rounded-3xl border border-border bg-card p-8" data-testid="class-how-it-works">
          <h2 className="font-serif text-lg md:text-lg">How it works</h2>
          <ol className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li>1. Sign up as a teacher with your school email and verify it.</li>
            <li>2. Create a class and buy a licence for the course you want to teach.</li>
            <li>3. Share the class code. Students join and start learning straight away.</li>
          </ol>
          <p className="mt-5 text-sm text-muted-foreground">Know a music teacher who'd love this? Use <span className="font-semibold text-foreground">Share with a teacher</span> to send them this page.</p>
        </section>
        <TeacherTestimonials fallback={TEACHER_SAMPLES} className="mt-14" />
        <section className="mt-14 rounded-3xl border border-border bg-card p-8" data-testid="class-enquiry-section">
          <h2 className="font-serif text-lg md:text-lg">Got a question first?</h2>
          <p className="mt-2 text-sm text-muted-foreground">Ask Jason about class sizes, instruments or getting started. He'll reply by email.</p>
          <TeacherEnquiryForm />
        </section>
      </main>
    </div>
  );
}
